import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthProvider";
import { experientialItems, ToolkitItem } from "./catalog";
import { usePromptTechniques } from "./usePromptTechniques";
import { TechniqueGrid } from "./TechniqueGrid";
import { PromptWorkspace } from "./PromptWorkspace";
import { ExperientialWorkspace } from "./ExperientialWorkspace";
import "./Toolkit.css";

type Props = {
  patientId: string;
  sessionId?: string;
  language: "en" | "hu";
  apiBaseUrl: string;
  getToken: () => Promise<string>;
};

function readItemParam(): string | null {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  return params.get("item");
}

function writeItemParam(itemId: string | null) {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  if (itemId) params.set("item", itemId);
  else params.delete("item");
  // Assigning location.hash pushes a history entry → browser Back works.
  window.location.hash = params.toString();
}

export const ToolkitPage: React.FC<Props> = ({ patientId, sessionId, language, apiBaseUrl, getToken }) => {
  const { t } = useTranslation();
  const { ensureToken } = useAuth();
  const prompts = usePromptTechniques(apiBaseUrl, ensureToken);
  const expItems = useMemo(() => experientialItems(), []);
  const allItems = useMemo(() => [...prompts.items, ...expItems], [prompts.items, expItems]);

  const [openId, setOpenId] = useState<string | null>(() => readItemParam());
  // True while the current history entry is the one WE pushed in open();
  // lets back() pop that entry instead of pushing yet another one.
  const pushedRef = useRef(false);
  // Hash string we wrote ourselves in open(). Assigning location.hash is
  // synchronous, but the resulting hashchange event fires asynchronously —
  // without this marker the handler below would mistake our own write for
  // external navigation and clear pushedRef, making back() always take the
  // replaceState path in real browsers.
  const selfHashRef = useRef<string | null>(null);

  useEffect(() => {
    const onHash = () => {
      setOpenId(readItemParam());
      if (window.location.hash === selfHashRef.current) {
        // Our own open() write arriving — push bookkeeping stays valid.
        selfHashRef.current = null;
        return;
      }
      // Genuine hash navigation (browser back/forward) invalidates our push
      // bookkeeping — the current entry is no longer the one we pushed.
      pushedRef.current = false;
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const open = useCallback((item: ToolkitItem) => {
    writeItemParam(item.id);
    selfHashRef.current = window.location.hash; // includes leading '#'
    pushedRef.current = true;
    setOpenId(item.id);
  }, []);

  const back = useCallback(() => {
    selfHashRef.current = null; // any pending self-write marker is now stale
    if (pushedRef.current) {
      // We pushed the item entry ourselves — pop it so browser Back
      // doesn't land on the workspace the user just closed.
      pushedRef.current = false;
      window.history.back(); // hashchange listener clears openId
      return;
    }
    // Deep link / restored session: no entry of ours to pop — rewrite in place.
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.hash.replace(/^#/, ""));
    params.delete("item");
    url.hash = params.toString();
    window.history.replaceState(null, "", url.toString());
    setOpenId(null);
  }, []);

  const openItem = openId ? allItems.find((i) => i.id === openId) : undefined;
  // An item id in the hash that matches nothing (stale link, prompts still
  // loading) falls through to the grid; once prompts load, the effect of
  // allItems changing re-derives openItem naturally.

  return (
    <div className="tk-page">
      {/* Grid stays mounted so search/filter/scroll state survives back. */}
      <div style={{ display: openItem ? "none" : undefined }}>
        {prompts.loading && <p className="tk-empty">{t("toolkit.loading")}</p>}
        {prompts.error && (
          <div className="tk-error">
            {prompts.error}{" "}
            <button type="button" className="tk-chip" onClick={prompts.reload}>{t("toolkit.retry")}</button>
          </div>
        )}
        {!prompts.loading && <TechniqueGrid items={allItems} onOpen={open} />}
      </div>
      {openItem && openItem.kind === "prompt" && (
        /* key includes patientId: workspaces seed local client state at mount,
           so a global client switch must remount them (stale-client guard). */
        <PromptWorkspace
          key={`${openItem.id}:${patientId}`}
          item={openItem} patientId={patientId} apiBaseUrl={apiBaseUrl} getToken={getToken} onBack={back}
        />
      )}
      {openItem && openItem.kind === "experiential" && (
        <ExperientialWorkspace
          key={`${openItem.id}:${patientId}`}
          item={openItem} patientId={patientId} sessionId={sessionId}
          language={language} apiBaseUrl={apiBaseUrl} getToken={getToken} onBack={back}
        />
      )}
    </div>
  );
};
